import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { fileToBase64 } from "@/lib/format";
import { Mail, Phone, Building2, MapPin, LogOut, Edit, X, Camera, Share2, ExternalLink, Check, BellRing } from "lucide-react";
import ZButton from "@/components/ZButton";
import { ZInput, ZSelect, ZTextarea } from "@/components/ZInput";
import VerifiedBadge from "@/components/VerifiedBadge";
import OrgVerificationCard from "@/components/OrgVerificationCard";
import { toast } from "sonner";

const ORG_TYPES = [
  { value: "particulier", label: "Particulier" },
  { value: "association", label: "Association" },
  { value: "entreprise", label: "Entreprise" },
  { value: "institution", label: "Institution" },
];

function Avatar({ user, size = 64 }) {
  if (user.logo_b64) {
    return (
      <img
        src={user.logo_b64}
        alt={user.org_name || user.name}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: "var(--z-teal)",
      color: "white", display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.4, fontWeight: 700,
    }}>
      {user.name?.[0]?.toUpperCase()}
    </div>
  );
}

function ReadView({ user, onEdit, onLogout }) {
  const [copied, setCopied] = useState(false);
  const publicUrl = user.slug ? `${window.location.origin}/o/${user.slug}` : null;
  const copy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };
  return (
    <>
      <div className="z-card" style={{ padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
          <Avatar user={user} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 18, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid="org-profile-name">
              {user.org_name || user.name}
              <VerifiedBadge verified={user.verified} variant="pill" testId="org-profile-verified" />
            </div>
            <div style={{ fontSize: 13, color: "var(--z-muted)" }}>
              {user.org_type ? ORG_TYPES.find((t) => t.value === user.org_type)?.label || user.org_type : "Organisateur"}
            </div>
          </div>
          <button type="button" onClick={onEdit} className="z-btn z-btn-outline z-btn-sm" data-testid="org-profile-edit-btn">
            <Edit size={14} /> Modifier
          </button>
        </div>

        {publicUrl && (
          <div style={{ background: "linear-gradient(135deg,#01696F 0%,#023d40 100%)", color: "white", padding: 14, borderRadius: 12, marginBottom: 14 }} data-testid="org-profile-public-card">
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, opacity: 0.85, marginBottom: 4 }}>
              <Share2 size={14} /> Votre page publique
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <a href={publicUrl} target="_blank" rel="noreferrer" style={{ color: "white", fontWeight: 600, textDecoration: "none", fontSize: 14, wordBreak: "break-all" }} data-testid="org-profile-public-link">
                {publicUrl} <ExternalLink size={12} style={{ verticalAlign: "middle" }} />
              </a>
              <button type="button" onClick={copy} className="z-btn z-btn-sm" style={{ background: "white", color: "var(--z-teal)", marginLeft: "auto" }} data-testid="org-profile-public-copy-btn">
                {copied ? <><Check size={12} /> Copié</> : "Copier"}
              </button>
            </div>
          </div>
        )}

        <Row icon={<Mail size={16} />} label={user.email} />
        {user.phone && <Row icon={<Phone size={16} />} label={user.phone} />}
        {user.org_name && <Row icon={<Building2 size={16} />} label={user.org_name} />}
        {user.city && <Row icon={<MapPin size={16} />} label={user.city} />}
        {user.bio && (
          <div style={{ padding: "14px 0 0", marginTop: 4, borderTop: "1px solid var(--z-border)", fontSize: 14, color: "var(--z-text)", lineHeight: 1.6, whiteSpace: "pre-wrap" }} data-testid="org-profile-bio">
            {user.bio}
          </div>
        )}
      </div>

      {user.role === "organizer" && <OrgVerificationCard user={user} />}

      <ZButton variant="danger" onClick={onLogout} style={{ marginTop: 18 }} block data-testid="org-logout-btn">
        <LogOut size={16} /> Se déconnecter
      </ZButton>

      <Link to="/profile/notifications" className="z-btn z-btn-outline" style={{ marginTop: 10, width: "100%" }} data-testid="org-profile-notifs-link">
        <BellRing size={16} /> Préférences de notifications
      </Link>
    </>
  );
}

function EditForm({ user, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    phone: user.phone || "",
    org_name: user.org_name || "",
    org_type: user.org_type || "",
    city: user.city || "",
    bio: user.bio || "",
    logo_b64: user.logo_b64 || "",
    slug: user.slug || "",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_000_000) {
      setErr("Logo trop volumineux (max 1 Mo).");
      return;
    }
    setErr("");
    setUploading(true);
    try {
      const b64 = await fileToBase64(f);
      set("logo_b64", b64);
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () => set("logo_b64", "");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Le nom est obligatoire.");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        org_name: form.org_name.trim() || null,
        org_type: form.org_type || null,
        city: form.city.trim() || null,
        bio: form.bio.trim() || null,
        logo_b64: form.logo_b64 || "",
        slug: form.slug.trim().toLowerCase(),
      };
      const res = await api.put("/auth/me", payload);
      onSaved(res.data);
      toast.success("Profil mis à jour");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="z-card" style={{ padding: 24 }} data-testid="org-profile-edit-form">
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
        <div style={{ position: "relative" }}>
          <Avatar user={form.logo_b64 ? { logo_b64: form.logo_b64, name: form.name } : { name: form.name }} size={80} />
          <label style={{
            position: "absolute", bottom: 0, right: 0, background: "var(--z-orange)", color: "white",
            width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer", border: "2px solid white",
          }} data-testid="org-profile-logo-pick">
            <Camera size={14} />
            <input type="file" accept="image/*" onChange={onLogoFile} style={{ display: "none" }} data-testid="org-profile-logo-input" />
          </label>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: "var(--z-muted)", marginBottom: 4 }}>Logo (optionnel, max 1 Mo)</div>
          {form.logo_b64 && (
            <button type="button" onClick={removeLogo} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error,#c00)" }} data-testid="org-profile-logo-remove">
              <X size={12} /> Retirer
            </button>
          )}
          {uploading && <span style={{ fontSize: 12, color: "var(--z-muted)" }}>Chargement…</span>}
        </div>
      </div>

      <ZInput label="Nom complet" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="org-profile-name-input" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <ZInput label="Téléphone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="org-profile-phone-input" />
        <ZInput label="Ville" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Conakry" data-testid="org-profile-city-input" />
      </div>
      <ZInput label="Nom de la structure" value={form.org_name} onChange={(e) => set("org_name", e.target.value)} placeholder="Mon Asso, Ma Société…" data-testid="org-profile-orgname-input" />
      <ZSelect label="Type d'organisation" value={form.org_type} onChange={(e) => set("org_type", e.target.value)} data-testid="org-profile-orgtype-input">
        <option value="">— Non précisé —</option>
        {ORG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </ZSelect>
      <ZTextarea label="Bio (présentation publique, 500 caractères max)" value={form.bio} onChange={(e) => set("bio", e.target.value.slice(0, 500))} placeholder="Décrivez votre activité, votre mission, votre histoire…" rows={4} data-testid="org-profile-bio-input" />
      <p className="z-help" style={{ marginTop: -10 }}>{form.bio.length}/500</p>

      <ZInput
        label="URL personnalisée (slug de votre page publique)"
        value={form.slug}
        onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
        placeholder="ex: zefest-productions"
        data-testid="org-profile-slug-input"
      />
      <p className="z-help" style={{ marginTop: -10 }}>
        Votre page publique : <strong>{window.location.origin}/o/{form.slug || "votre-slug"}</strong>
      </p>

      {err && <p className="z-error-text" data-testid="org-profile-error">{err}</p>}

      <div style={{ display: "flex", gap: 10 }}>
        <ZButton type="button" variant="outline" block onClick={onCancel} data-testid="org-profile-cancel-btn">Annuler</ZButton>
        <ZButton type="submit" block loading={saving} data-testid="org-profile-save-btn">Enregistrer</ZButton>
      </div>
    </form>
  );
}

export default function OrgProfilePage() {
  const { user, logout, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  if (!user) return null;

  return (
    <div className="z-container" data-testid="org-profile-page">
      <h1 className="z-page-title">Profil organisateur</h1>
      <p className="z-page-sub">{editing ? "Mettez à jour vos informations publiques." : "Informations de votre compte."}</p>

      {editing ? (
        <EditForm
          user={user}
          onCancel={() => setEditing(false)}
          onSaved={(u) => { setUser(u); setEditing(false); }}
        />
      ) : (
        <ReadView user={user} onEdit={() => setEditing(true)} onLogout={logout} />
      )}
    </div>
  );
}

function Row({ icon, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid var(--z-border)" }}>
      <span style={{ color: "var(--z-muted)" }}>{icon}</span>
      <span>{label}</span>
    </div>
  );
}
